import _extends from "@babel/runtime/helpers/esm/extends";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
/* eslint-disable react-hooks-ssr/react-hooks-global-ssr */

import React, { useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { useMotionValue, motion } from 'framer-motion';
import { useForkedRef, useIsomorphicLayoutEffect } from "../../hooks";
import { sharedInputStyles, sharedInputContainerStyles } from "../TextField/TextField.styles";
import { theme } from "../../theme";
import { useTrackFocus } from "../TextField/useTrackFocus";
import useResizeObserver from 'use-resize-observer';
import { useId } from '@reach/auto-id';
import { TextFieldLabel } from "../TextField";
import { Stack } from "../Stack";
import { textStyles } from "../Text/Text";
export var TextareaField = React.forwardRef(function (_ref, forwardedRef) {
  var idProp = _ref.id,
      label = _ref.label,
      _ref$autosize = _ref.autosize,
      autoSizeProp = _ref$autosize === void 0 ? true : _ref$autosize,
      onChangeProp = _ref.onChange,
      _ref$rowsMax = _ref.rowsMax,
      rowsMax = _ref$rowsMax === void 0 ? 5 : _ref$rowsMax,
      _ref$rowsMin = _ref.rowsMin,
      rowsMin = _ref$rowsMin === void 0 ? 2 : _ref$rowsMin,
      value = _ref.value,
      placeholder = _ref.placeholder,
      _ref$resize = _ref.resize,
      resize = _ref$resize === void 0 ? 'vertical' : _ref$resize,
      onFocusProp = _ref.onFocus,
      onBlurProp = _ref.onBlur,
      onMouseUpProp = _ref.onMouseUp,
      onMouseDownProp = _ref.onMouseDown,
      other = _objectWithoutProperties(_ref, ["id", "label", "autosize", "onChange", "rowsMax", "rowsMin", "value", "placeholder", "resize", "onFocus", "onBlur", "onMouseUp", "onMouseDown"]);

  if (rowsMin > rowsMax) {
    throw new Error('rowsMax must be greater or equal to rowsMin');
  }

  var id = useId(idProp);
  var autosize = !(rowsMax === rowsMin || autoSizeProp === false);
  var height = useMotionValue(0);
  var overflow = useMotionValue('hidden');

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      hasManuallyResized = _useState2[0],
      setHasManuallyResized = _useState2[1]; // refs


  var shadowRef = useRef(null);
  var textareaRef = useRef(null);
  var ref = useForkedRef(forwardedRef, textareaRef);
  var widthRef = useRef(null);
  var mouseDownRef = useRef(null);
  useResizeObserver({
    ref: textareaRef,
    onResize: function onResize(_ref2) {
      var width = _ref2.width;

      if (mouseDownRef.current || hasManuallyResized) {
        if (!hasManuallyResized) {
          setHasManuallyResized(true);
        }

        return;
      }

      if (width !== widthRef.current) {
        measureAndSetTextAreaDimensions();
      }

      widthRef.current = width;
    }
  }); // track focus state to match TextField behavior

  var _useTrackFocus = useTrackFocus({
    onFocusProp: onFocusProp,
    onBlurProp: onBlurProp
  }),
      focused = _useTrackFocus.focused,
      onFocus = _useTrackFocus.onFocus,
      onBlur = _useTrackFocus.onBlur;
  /**
   * Handle measuring and resizing the textarea.
   */


  function measureAndSetTextAreaDimensions() {
    var textarea = textareaRef.current;
    var shadow = shadowRef.current;

    if (!shadow) {
      return;
    }

    var computedStyle = window.getComputedStyle(textarea);
    shadow.style.width = computedStyle.width;
    shadow.value = textarea.value || placeholder || 'x';

    if (shadow.value.slice(-1) === '\n') {
      shadow.value += ' ';
    }

    var paddingTop = computedStyle.paddingTop,
        paddingBottom = computedStyle.paddingBottom;
    var totalVerticalPadding = getPaddingInt(paddingTop) + getPaddingInt(paddingBottom); // Height of content

    var innerHeight = shadow.scrollHeight; // Single line height

    shadow.value = 'x';
    var singleLineHeight = shadow.scrollHeight - totalVerticalPadding;
    var outerHeight = innerHeight;

    if (rowsMin) {
      outerHeight = Math.max(Number(rowsMin) * singleLineHeight + totalVerticalPadding, outerHeight);
    }

    if (rowsMax) {
      outerHeight = Math.min(Number(rowsMax) * singleLineHeight + totalVerticalPadding, outerHeight);
    }

    outerHeight = Math.max(outerHeight, singleLineHeight);
    var withinBounds = Math.abs(outerHeight - innerHeight) <= 1; // update our styles

    height.set(outerHeight);
    overflow.set(withinBounds ? 'hidden' : 'auto');
  }

  function onChange(e) {
    if (autosize && !hasManuallyResized) {
      measureAndSetTextAreaDimensions();
    }

    if (onChangeProp) {
      onChangeProp(e);
    }
  }

  useIsomorphicLayoutEffect(function () {
    if (autosize && !hasManuallyResized) {
      measureAndSetTextAreaDimensions();
    }
  });
  /**
   * Manually track whether the user has their
   * mouse down to determine if resize events are caused
   * by drag to resize. This is a bit hacky, but i
   * can't think of a better solve here.
   */

  function onMouseDown(e) {
    if (onMouseDownProp) {
      onMouseDownProp(e);
    }

    mouseDownRef.current = true;
  }

  function onMouseUp(e) {
    if (onMouseUpProp) {
      onMouseUpProp(e);
    }

    mouseDownRef.current = false;
  }

  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Stack, {
    gap: 4
  }, label && /*#__PURE__*/React.createElement(TextFieldLabel, {
    htmlFor: id
  }, label), /*#__PURE__*/React.createElement(StyledTextarea, _extends({
    rows: rowsMin,
    value: value,
    onChange: onChange,
    ref: ref,
    style: autosize && !hasManuallyResized ? {
      height: height,
      overflow: overflow
    } : undefined,
    placeholder: placeholder,
    $isFocused: focused,
    $resize: resize,
    onFocus: onFocus,
    onBlur: onBlur,
    "aria-label": label ? undefined : placeholder,
    onMouseDown: onMouseDown,
    onMouseUp: onMouseUp
  }, other))), autosize && /*#__PURE__*/React.createElement(TextShadow, {
    "aria-hidden": true,
    readOnly: true,
    ref: shadowRef,
    tabIndex: -1
  }));
});
var sharedStyles = css(["", ";", ";padding-top:", ";padding-bottom:", ";"], sharedInputStyles, textStyles.primary, theme.spaces[4], theme.spaces[4]);
var StyledTextarea = styled(motion.textarea).withConfig({
  displayName: "TextareaField__StyledTextarea",
  componentId: "vapor__sc-7o5p9v-0"
})(["resize:", ";", ";", ";color:", ";&::-webkit-resizer{background-repeat:no-repeat;background-position:50%;background-image:", ";}"], function (p) {
  return p.$resize;
}, sharedStyles, sharedInputContainerStyles, theme.colors.palette.tintA90, function (p) {
  return p.$isFocused ? "url(\"data:image/svg+xml,%3Csvg width='11' height='11' viewBox='0 0 11 11' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 10L10 1' stroke='%235B53FF' stroke-opacity='0.5' stroke-linecap='round'/%3E%3Cpath d='M5 10L10 5' stroke='%235B53FF' stroke-opacity='0.5' stroke-linecap='round'/%3E%3C/svg%3E%0A\")" : "url(\"data:image/svg+xml,%3Csvg width='11' height='11' viewBox='0 0 11 11' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 10L10 1' stroke='%233A3B4F' stroke-linecap='round'/%3E%3Cpath d='M5 10L10 5' stroke='%233A3B4F' stroke-linecap='round'/%3E%3C/svg%3E%0A\")";
});
var TextShadow = styled.textarea.withConfig({
  displayName: "TextareaField__TextShadow",
  componentId: "vapor__sc-7o5p9v-1"
})(["visibility:hidden;position:absolute;overflow:hidden;height:0;top:0;left:0;", ";"], sharedStyles);

function getPaddingInt(padding) {
  var _parseInt;

  return (_parseInt = parseInt(padding, 10)) !== null && _parseInt !== void 0 ? _parseInt : 0;
}